import React, { useState, useEffect } from 'react';
import { List, Avatar, Card, Empty, Button, Skeleton, Tag, Modal, Typography } from 'antd';
import { API_ENDPOINTS } from '../utils/config';
import { FaEnvelopeOpenText, FaSyncAlt, FaUser, FaRegCommentDots, FaClock } from 'react-icons/fa';
import '../css/admin/ContactInbox.css';





// Fetch all messages (API returns all data)
const fetchMessages = async () => {
  try {
    const response = await fetch(
      `${API_ENDPOINTS.ADMIN_BASE}/contact-us/messages/`
    );
    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errText}`);
    }
    const data = await response.json();
    return {
      messages: data.data || [],
      total: data.data?.length || 0,
    };
  } catch (e) {
    console.error('Failed to fetch contact messages', e);
    throw e;
  }
};



const ContactInbox = () => {
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const pageSize = 6; // items per load
  const [displayCount, setDisplayCount] = useState(pageSize);

  // Load messages from server
  const loadMessages = async (reset = true) => {
    setLoading(true);
    try {
      const data = await fetchMessages();
      setMessages(data.messages);
      setTotal(data.total);
      if (reset) {
        setDisplayCount(pageSize);
      }
    } catch (e) {
      console.error('Failed to load messages', e);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    setDisplayCount(prev => Math.min(prev + pageSize, total));
  };

  // Compute messages to display based on displayCount
  const displayedMessages = messages.slice(0, displayCount);

  useEffect(() => {
    loadMessages();
  }, []);

  return (
    <div className="contact-inbox">
      <Card
        title={<span><FaEnvelopeOpenText style={{ marginRight: 8 }} />Contact Inbox</span>}
        extra={<Button type="text" icon={<FaSyncAlt />} onClick={() => { loadMessages(true); }} />}
        className="inbox-card"
      >
        {loading && messages.length === 0 ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : messages.length === 0 ? (
          <Empty description="No messages" />
        ) : (
          <>
            <List
              itemLayout="horizontal"
              dataSource={displayedMessages}
              renderItem={item => (
                <List.Item actions={[<Button type="link" key="view" onClick={() => { setSelectedMessage(item); setIsModalVisible(true); }}>View</Button>]}>
                  <List.Item.Meta
                    avatar={<Avatar>{item.name.charAt(0)}</Avatar>}
                    title={
                      <span>
                        {item.subject}
                        {/* Show New tag for recent messages */}
                        {new Date() - new Date(item.created_at) < 5 * 60 * 1000 && (
                          <Tag className="new-tag" style={{ marginLeft: 8 }}>New</Tag>
                        )}
                      </span>
                    }
                    description={
                      <>
                        <span className="sender-name">{item.name}</span> • {item.message}
                      </>
                    }
                  />
                  <div className="timestamp">{new Date(item.created_at).toLocaleString()}</div>
                </List.Item>
              )}
            />
          </>
        )}
        {total > displayedMessages.length && displayedMessages.length > 0 && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button type="primary" onClick={loadMore} loading={loading}>Load More</Button>
          </div>
        )}
      </Card>
      <Modal
        open={isModalVisible}
        title={selectedMessage?.subject || 'Message Details'}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setIsModalVisible(false)}>
            Close
          </Button>,
        ]}
        className="contact-modal creative-modal"
        centered
        maskClosable={false}
        style={{ top: 40, maxWidth: 600 }}
        bodyStyle={{
          padding: '32px',
          background: 'linear-gradient(135deg, #ffecd2, #fcb69f)',
          borderRadius: '16px',
          textAlign: 'center',
        }}
      >
        {selectedMessage && (
          <div className="modal-content">
            <Typography.Title level={4} style={{ marginBottom: 12, color: '#b35c44' }}>
              {selectedMessage.subject}
            </Typography.Title>

            <Typography.Paragraph style={{ margin: '12px 0', fontSize: '1rem' }}>
              <FaUser style={{ marginRight: 6, color: '#b35c44' }} />
              <strong>From:</strong> {selectedMessage.name}
            </Typography.Paragraph>
            <Typography.Paragraph style={{ margin: '12px 0', fontSize: '1rem' }}>
              <FaEnvelopeOpenText style={{ marginRight: 6, color: '#b35c44' }} />
              <strong>Email:</strong> {selectedMessage.emailID}
            </Typography.Paragraph>
            <Typography.Paragraph style={{ margin: '12px 0', fontSize: '1rem' }}>
              <FaRegCommentDots style={{ marginRight: 6, color: '#b35c44' }} />
              <strong>Message:</strong> {selectedMessage.message}
            </Typography.Paragraph>

            <Typography.Paragraph style={{ margin: '12px 0', fontSize: '1rem' }}>
              <FaClock style={{ marginRight: 6, color: '#b35c44' }} />
              <strong>Received:</strong>{' '}{new Date(selectedMessage.created_at).toLocaleString()}
            </Typography.Paragraph>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ContactInbox;
